import { inject as injectBranding } from '@angular/core';
import { SiteBrandingService } from '../../services/site-branding.service';
import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormsModule, NgForm } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '../../../environments/environment';
import { Location } from '../../models/app.models';
import { LocationService } from '../../services/location.service';
import { ContactSettings, SiteSettingsService } from '../../services/site-setting.service';
import { ToastService } from '../../services/toast.service';

@Component({
  selector: 'app-contact-us',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './contact-us.component.html',
  styleUrl: './contact-us.component.css',
})
export class ContactUsComponent implements OnInit {
  readonly branding = injectBranding(SiteBrandingService);
  private readonly destroyRef = inject(DestroyRef);
  locations: Location[] = [];
  contactSettings: ContactSettings | null = null;
  locationsLoading = true;
  locationsError = false;
  settingsError = false;
  isSubmitting = false;
  statusMessage = '';
  submissionSucceeded = false;
  request = this.emptyRequest();

  constructor(
    private readonly http: HttpClient,
    private readonly locationService: LocationService,
    private readonly settingsService: SiteSettingsService,
    private readonly toastService: ToastService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadLocations();
    this.settingsService.getContactSettings().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: settings => { this.contactSettings = settings; this.cdr.markForCheck(); },
      error: () => { this.settingsError = true; this.cdr.markForCheck(); },
    });
  }

  get emailLink(): string {
    return `mailto:${this.contactSettings?.contactEmail.trim() ?? ''}`;
  }

  get phoneLink(): string {
    return `tel:${this.contactSettings?.contactPhone.replace(/[^\d+]/g, '') ?? ''}`;
  }

  loadLocations(): void {
    this.locationsLoading = true;
    this.locationsError = false;
    this.locationService.getLocations().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: locations => {
        this.locations = locations.filter(location => location.isActive)
          .sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name));
        this.locationsLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.locationsLoading = false;
        this.locationsError = true;
        this.cdr.markForCheck();
      },
    });
  }

  submitContactRequest(form: NgForm): void {
    if (this.isSubmitting) return;
    if (form.invalid || !this.request.name.trim() || !this.request.message.trim()
      || !this.locations.some(location => location.id === this.request.locationId)) {
      form.control.markAllAsTouched();
      this.toastService.error('Enter all required fields and select a restaurant.');
      return;
    }
    const payload = {
      ...this.request,
      name: this.request.name.trim(),
      email: this.request.email.trim(),
      phone: this.request.phone.trim(),
      message: this.request.message.trim(),
    };
    this.isSubmitting = true;
    this.statusMessage = '';
    this.submissionSucceeded = false;
    this.http.post(`${environment.apiUrl}/contact`, payload)
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.isSubmitting = false;
          this.submissionSucceeded = true;
          this.statusMessage = 'Your message has been sent. Thank you for sharing your feedback.';
          this.request = this.emptyRequest();
          form.resetForm(this.request);
          this.toastService.success(this.statusMessage);
          this.cdr.markForCheck();
        },
        error: error => {
          this.isSubmitting = false;
          this.statusMessage = typeof error.error?.message === 'string'
            ? error.error.message : 'Unable to send your message. Please try again or contact us directly.';
          this.toastService.error(this.statusMessage);
          this.cdr.markForCheck();
        },
      });
  }

  private emptyRequest() {
    return { name: '', email: '', phone: '', locationId: null as number | null, message: '', wishToBeContacted: false };
  }
}

